import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Table,
  Badge,
  Modal,
  Form,
  Row,
  Col,
  InputGroup,
  Spinner,
  // Alert,
} from "react-bootstrap";
import {
  IconHistory,
  IconSearch,
  // IconFilter,
  // IconCalendar,
  // IconPackage,
  IconPlus,
  IconMinus,
  // IconEye,
} from "@tabler/icons-react";
import { useInventory } from "../context/InventoryContext";

const InventoryHistory = ({ show, onHide }) => {
  const { inventory } = useInventory();
  // const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all"); // all, add, subtract
  const [dateRange, setDateRange] = useState({
    start: "",
    end: "",
  });

  // Mock history data - in real app this would come from API
  const [historyData, setHistoryData] = useState([]);

  useEffect(() => {
    if (inventory.data) {
      // Generate mock history data based on current inventory
      const mockHistory = generateMockHistory(inventory.data);
      setHistoryData(mockHistory);
    }
  }, [inventory.data]);

  const generateMockHistory = (items) => {
    const history = [];
    const types = ["add", "subtract"];
    const reasons = [
      "Restock from supplier",
      "Used in service order #1234",
      "Used in service order #5678",
      "Inventory adjustment",
      "Return from service",
      "Monthly stock count",
      "Damaged item replacement",
      "New shipment arrival",
    ];

    items.forEach((item) => {
      // Generate 3-8 random history entries per item
      const numEntries = Math.floor(Math.random() * 6) + 3;

      for (let i = 0; i < numEntries; i++) {
        const type = types[Math.floor(Math.random() * types.length)];
        const quantity = Math.floor(Math.random() * 50) + 1;
        const reason = reasons[Math.floor(Math.random() * reasons.length)];

        // Generate random date within last 90 days
        const daysAgo = Math.floor(Math.random() * 90);
        const date = new Date();
        date.setDate(date.getDate() - daysAgo);

        history.push({
          id: `${item.id}_${i}`,
          itemId: item.id,
          itemName: item.name,
          type,
          quantity,
          reason,
          previousStock: Math.max(0, item.stock - quantity),
          newStock: item.stock,
          timestamp: date.toISOString(),
          user: "System", // In real app, this would be the user who made the change
        });
      }
    });

    // Sort by timestamp descending
    return history.sort(
      (a, b) => new Date(b.timestamp) - new Date(a.timestamp),
    );
  };

  const filteredHistory = historyData.filter((entry) => {
    const matchesSearch = entry.itemName
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesType = filterType === "all" || entry.type === filterType;
    const matchesDate =
      (!dateRange.start ||
        new Date(entry.timestamp) >= new Date(dateRange.start)) &&
      (!dateRange.end || new Date(entry.timestamp) <= new Date(dateRange.end));

    return matchesSearch && matchesType && matchesDate;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("id-ID", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getTypeIcon = (type) => {
    return type === "add" ? (
      <IconPlus size={16} color="#10b981" />
    ) : (
      <IconMinus size={16} color="#ef4444" />
    );
  };

  const getTypeBadge = (type) => {
    return (
      <Badge bg={type === "add" ? "success" : "danger"}>
        {type === "add" ? "Stock In" : "Stock Out"}
      </Badge>
    );
  };

  return (
    <Modal className="workshop-modal-scope" show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <IconHistory size={20} style={{ marginRight: "8px" }} />
          Inventory Usage History
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Filters */}
        <Card className="mb-3">
          <Card.Body>
            <Row className="g-3">
              <Col md={4}>
                <InputGroup>
                  <InputGroup.Text>
                    <IconSearch size={16} />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Search items..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md={3}>
                <Form.Select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  <option value="all">All Types</option>
                  <option value="add">Stock In</option>
                  <option value="subtract">Stock Out</option>
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Control
                  type="date"
                  placeholder="Start Date"
                  value={dateRange.start}
                  onChange={(e) =>
                    setDateRange((prev) => ({ ...prev, start: e.target.value }))
                  }
                />
              </Col>
              <Col md={2}>
                <Form.Control
                  type="date"
                  placeholder="End Date"
                  value={dateRange.end}
                  onChange={(e) =>
                    setDateRange((prev) => ({ ...prev, end: e.target.value }))
                  }
                />
              </Col>
              <Col md={1}>
                <Button
                  variant="outline-secondary"
                  onClick={() => {
                    setSearchTerm("");
                    setFilterType("all");
                    setDateRange({ start: "", end: "" });
                  }}
                >
                  Clear
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* History Table */}
        <Card>
          <Card.Body style={{ padding: 0 }}>
            <Table striped hover style={{ marginBottom: 0 }}>
              <thead>
                <tr>
                  <th>Date & Time</th>
                  <th>Item</th>
                  <th style={{ textAlign: "center" }}>Type</th>
                  <th style={{ textAlign: "center" }}>Quantity</th>
                  <th>Reason</th>
                  <th style={{ textAlign: "center" }}>Previous Stock</th>
                  <th style={{ textAlign: "center" }}>New Stock</th>
                  <th>User</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.length > 0 ? (
                  filteredHistory.map((entry) => (
                    <tr key={entry.id}>
                      <td style={{ fontSize: "14px", color: "#6b7280" }}>
                        {formatDate(entry.timestamp)}
                      </td>
                      <td>
                        <div style={{ fontWeight: "500" }}>
                          {entry.itemName}
                        </div>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "4px",
                          }}
                        >
                          {getTypeIcon(entry.type)}
                          {getTypeBadge(entry.type)}
                        </div>
                      </td>
                      <td style={{ textAlign: "center", fontWeight: "500" }}>
                        {entry.quantity}
                      </td>
                      <td style={{ maxWidth: "200px" }}>
                        <div
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={entry.reason}
                        >
                          {entry.reason}
                        </div>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {entry.previousStock}
                      </td>
                      <td style={{ textAlign: "center", fontWeight: "500" }}>
                        {entry.newStock}
                      </td>
                      <td>{entry.user}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan="8"
                      style={{ textAlign: "center", padding: "40px" }}
                    >
                      {inventory.loading ? (
                        <div>
                          <Spinner animation="border" />
                          <div style={{ marginTop: "8px" }}>
                            Loading history...
                          </div>
                        </div>
                      ) : (
                        <div>
                          <IconHistory size={48} color="#6b7280" />
                          <div style={{ marginTop: "16px", color: "#6b7280" }}>
                            No history records found
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {/* Summary Stats */}
        {filteredHistory.length > 0 && (
          <Card className="mt-3">
            <Card.Body>
              <Row>
                <Col md={3}>
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#10b981",
                      }}
                    >
                      {filteredHistory.filter((h) => h.type === "add").length}
                    </div>
                    <div style={{ color: "#6b7280", fontSize: "14px" }}>
                      Stock In
                    </div>
                  </div>
                </Col>
                <Col md={3}>
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#ef4444",
                      }}
                    >
                      {
                        filteredHistory.filter((h) => h.type === "subtract")
                          .length
                      }
                    </div>
                    <div style={{ color: "#6b7280", fontSize: "14px" }}>
                      Stock Out
                    </div>
                  </div>
                </Col>
                <Col md={3}>
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#3b82f6",
                      }}
                    >
                      {filteredHistory.reduce((sum, h) => sum + h.quantity, 0)}
                    </div>
                    <div style={{ color: "#6b7280", fontSize: "14px" }}>
                      Total Quantity
                    </div>
                  </div>
                </Col>
                <Col md={3}>
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#8b5cf6",
                      }}
                    >
                      {new Set(filteredHistory.map((h) => h.itemId)).size}
                    </div>
                    <div style={{ color: "#6b7280", fontSize: "14px" }}>
                      Items Modified
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default InventoryHistory;
